/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { PRIVILEGES } from 'constants/privileges';
import { AlertsContextState } from 'contexts/alertsContext/alertsContext.types';
import { i18n as TypeI18n } from 'i18next';
import { generatePath } from 'react-router-dom';
import {
  AdminTab,
  EventsTab,
  FilesTab,
  iframePaths,
  InvoiceTab,
  RatesTab,
  navbarPathsMap,
  PricesTab,
  reactPaths,
  ReportsTab,
  SettingsTab,
  TableTab,
} from 'root/pathnames';

interface NavOptionBase {
  name: string;
  title: string;
  url: string;
  value: string; // NOTE: menuId (for nav-menus) or path (for nav-items) to match
  icon?: string | null;
  requiredRoles?: null | { name: string }[];
  alertsContextKey?: Exclude<keyof AlertsContextState, 'alertsInbox'>;
}

export interface NavMenu extends NavOptionBase {
  url: '';
  menuItems: NavOptionBase[];
}

interface NavItem extends NavOptionBase {
  menuItems?: null;
}

export type NavOption = NavMenu | NavItem;

export type NavItems<Names extends string> = { [Property in Names]: NavItem };
type NavMenus<Names extends string> = { [Property in Names]: NavMenu };

export type DefaultNavItemsNames =
  | 'home'
  | 'inquiries'
  | 'orders'
  | 'offers'
  | 'jobs'
  | 'invoices'
  | 'rates'
  | 'transportCloud'
  | 'reports'
  | 'files'
  | 'tables'
  | 'prices'
  | 'calendar'
  | 'alerts'
  | 'account'
  | 'administration'
  | 'settings'
  | 'mapping';

const getDefaultNavItems = (
  i18n: TypeI18n,
): NavItems<DefaultNavItemsNames> => ({
  home: {
    name: 'main-menu-home',
    title: 'Storm',
    url: generatePath(navbarPathsMap.get('home')!),
    value: navbarPathsMap.get('home')!,
  },
  inquiries: {
    name: 'main-menu-requests-for-offers-tab',
    title: i18n.t('inquiries', { ns: 'menu' }),
    url: generatePath(navbarPathsMap.get('clientRequests')!),
    value: navbarPathsMap.get('clientRequests')!,
    icon: 'icons-file-pen',
    requiredRoles: [PRIVILEGES.Requests.VIEW_LIST],
    alertsContextKey: 'requestForOffersCount',
  },
  orders: {
    name: 'main-menu-orders-tab',
    title: i18n.t('orders', { ns: 'menu' }),
    url: generatePath(navbarPathsMap.get('clientOrders')!),
    value: navbarPathsMap.get('clientOrders')!,
    icon: 'icon-external-link',
    requiredRoles: [PRIVILEGES.Orders.VIEW_ORDER_LIST],
    alertsContextKey: 'ordersCount',
  },
  offers: {
    name: 'main-menu-offers-tab',
    title: i18n.t('offers', { ns: 'menu' }),
    url: generatePath(navbarPathsMap.get('offers')!),
    value: navbarPathsMap.get('offers')!,
    icon: 'icons-new-copy2',
    requiredRoles: [PRIVILEGES.Offers.VIEW_OFFER_LIST],
  },
  jobs: {
    name: 'main-menu-jobs-tab',
    title: i18n.t('jobs', { ns: 'menu' }),
    url: generatePath(navbarPathsMap.get('jobs')!),
    value: navbarPathsMap.get('jobs')!,
    icon: 'icons-briefcase',
    requiredRoles: [PRIVILEGES.Jobs.VIEW_JOB_LIST],
  },
  invoices: {
    name: 'main-menu-invoices-tab',
    title: i18n.t('invoices', { ns: 'menu' }),
    // NOTE: rule exception - reactPaths used instead of navbarPathsMap
    url: generatePath(reactPaths.invoicesModule, {
      tab: InvoiceTab.OWN,
    }),
    value: navbarPathsMap.get('invoices')!,
    icon: 'icon-dollar',
    requiredRoles: [PRIVILEGES.Invoices.VIEW_INVOICE_LIST],
  },
  rates: {
    name: 'main-menu-rates-tab',
    title: i18n.t('rates', { ns: 'menu' }),
    // NOTE: rule exception - reactPaths used instead of navbarPathsMap
    url: generatePath(reactPaths.ratesModule, {
      tab: RatesTab.OWN,
    }),
    value: navbarPathsMap.get('rates')!,
    icon: 'icon-balance',
  },
  transportCloud: {
    name: 'main-menu-transport-cloud-tab',
    title: i18n.t('transportCloud', { ns: 'menu' }),
    url: generatePath(reactPaths.transportCloud),
    value: navbarPathsMap.get('transportCloud')!,
    icon: 'icon-cloud',
    requiredRoles: [PRIVILEGES.TransportCloud.VIEW_TRANSPORT_CLOUD_LIST],
  },
  reports: {
    name: 'main-menu-reports-tab',
    title: i18n.t('reports', { ns: 'menu' }),
    url: generatePath(iframePaths.reportsTab, {
      tab: ReportsTab.ACTIVITY,
    }),
    value: navbarPathsMap.get('reports')!,
    icon: 'icons-chart',
    requiredRoles: [
      PRIVILEGES.Reports.VIEW_ACTIVITY_REPORT,
      PRIVILEGES.Reports.VIEW_CONTAINERS_REPORT,
      PRIVILEGES.Reports.VIEW_FORECAST,
      PRIVILEGES.Reports.VIEW_INVOICES_REPORT,
      PRIVILEGES.Reports.VIEW_JOB_REPORT,
      PRIVILEGES.Reports.VIEW_OFFERS_REPORT,
    ],
  },
  files: {
    name: 'main-menu-counterparties-tab',
    title: i18n.t('counterparties', { ns: 'menu' }),
    url: generatePath(iframePaths.filesTab, {
      tab: FilesTab.COUNTERPARTIES,
    }),
    value: navbarPathsMap.get('files')!,
  },
  tables: {
    name: 'main-menu-tables-tab',
    title: i18n.t('tables', { ns: 'menu' }),
    url: generatePath(iframePaths.tableTab, { tab: TableTab.CURRENCIES }),
    value: navbarPathsMap.get('tables')!,
  },
  prices: {
    name: 'main-menu-prices-tab',
    title: i18n.t('prices', { ns: 'menu' }),
    url: generatePath(iframePaths.pricesTab, {
      tab: PricesTab.TERMINAL_PRICES,
    }),
    value: navbarPathsMap.get('prices')!,
  },
  mapping: {
    name: 'main-menu-mapping-tab',
    title: i18n.t('mapping', { ns: 'menu' }),
    url: generatePath(reactPaths.mapping),
    value: navbarPathsMap.get('mapping')!,
    requiredRoles: [PRIVILEGES.Settings.VIEW_DISCRIMINANT_LIST],
  },
  calendar: {
    name: 'admin-menu-calendar',
    title: '',
    url: generatePath(iframePaths.eventsTab, {
      tab: EventsTab.CALENDAR,
    }),
    value: navbarPathsMap.get('events')!,
    icon: 'icons-calendar',
    requiredRoles: [PRIVILEGES.Calendar.MANAGE_CALENDAR],
  },
  alerts: {
    name: 'admin-menu-alerts',
    title: '',
    url: generatePath(navbarPathsMap.get('home')!),
    value: 'alerts',
    icon: 'icon-envelope',
    alertsContextKey: 'alertsCount',
  },
  account: {
    name: 'admin-menu-account',
    title: i18n.t('account', { ns: 'menu' }),
    url: generatePath(iframePaths.account),
    value: navbarPathsMap.get('account')!,
    requiredRoles: [
      PRIVILEGES.UserAccountManagement.DATA_SECTION,
      PRIVILEGES.UserAccountManagement.EMAIL_SECTION,
      PRIVILEGES.UserAccountManagement.PASSWORD_SECTION,
      PRIVILEGES.UserAccountManagement.REPLACEMENT_SECTION,
    ],
  },
  administration: {
    name: 'admin-menu-administration',
    title: i18n.t('administration', { ns: 'menu' }),
    url: generatePath(iframePaths.adminTab, {
      tab: AdminTab.GROUPS,
    }),
    value: navbarPathsMap.get('admin')!,
    // icon: 'icon-cog',
    requiredRoles: [
      PRIVILEGES.Admin.VIEW_USER_LIST,
      PRIVILEGES.Admin.VIEW_BRANCHES_LIST,
      PRIVILEGES.Admin.VIEW_GROUP_LIST,
      PRIVILEGES.Admin.VIEW_PARAMETER_LIST,
      PRIVILEGES.Admin.VIEW_PRIMARY_COUNTERPARTY,
      PRIVILEGES.Admin.VIEW_ROLE_LIST,
      PRIVILEGES.Admin.JPK,
      PRIVILEGES.Admin.VIEW_SERVICE_TERMS_LIST,
    ],
  },
  settings: {
    name: 'admin-menu-settings',
    title: i18n.t('settings', { ns: 'menu' }),
    url: generatePath(iframePaths.settingsTab, {
      tab: SettingsTab.BCT,
    }),
    value: navbarPathsMap.get('settings')!,
    // icon: 'icon-wrench',
    requiredRoles: [PRIVILEGES.Settings.VIEW_DISCRIMINANT_LIST],
  },
});

type DefaultNavMenusNames = 'client' | 'files' | 'user';

export const getDefaultNavOptions = (i18n: TypeI18n) => {
  const navItems = getDefaultNavItems(i18n);
  const navMenus: NavMenus<DefaultNavMenusNames> = {
    client: {
      name: 'main-menu-client-dropdown',
      title: i18n.t('client', { ns: 'menu' }),
      url: '',
      value: 'submenu-client',
      icon: null,
      requiredRoles: [
        PRIVILEGES.Requests.VIEW_LIST,
        PRIVILEGES.Orders.VIEW_ORDER_LIST,
      ],
      menuItems: [navItems.inquiries, navItems.orders],
    },
    files: {
      name: 'main-menu-files-dropdown',
      title: i18n.t('files', { ns: 'menu' }),
      url: '',
      value: 'submenu-files',
      icon: 'icons-library',
      menuItems: [
        navItems.files,
        navItems.tables,
        navItems.prices,
        navItems.mapping,
      ],
    },
    user: {
      name: 'admin-menu-user-account',
      title: '',
      url: '',
      value: 'submenu-user',
      icon: 'icon-user',
      menuItems: [navItems.account, navItems.administration, navItems.settings],
    },
  };

  return [navItems, navMenus] as const;
};

type ClientNavItemsNames =
  | 'home'
  | 'inquiries'
  | 'offers'
  | 'orders'
  | 'jobs'
  | 'invoices'
  | 'reports'
  | 'newsletter'
  | 'account'
  | 'alerts'
  | 'logout';

export const getClientNavItems = (
  i18n: TypeI18n,
): NavItems<ClientNavItemsNames> => ({
  home: {
    name: 'main-menu-home',
    title: 'Storm',
    url: generatePath(navbarPathsMap.get('home')!),
    value: navbarPathsMap.get('home')!,
  },
  inquiries: {
    name: 'main-menu-requests-for-offers-tab',
    title: i18n.t('inquiries', { ns: 'menu' }),
    url: generatePath(navbarPathsMap.get('clientRequests')!),
    value: navbarPathsMap.get('clientRequests')!,
    icon: 'icons-file-pen',
  },
  offers: {
    name: 'main-menu-offers-tab',
    title: i18n.t('offers', { ns: 'menu' }),
    url: generatePath(navbarPathsMap.get('offers')!),
    value: navbarPathsMap.get('offers')!,
    icon: 'icons-new-copy2',
  },
  orders: {
    name: 'main-menu-orders-tab',
    title: i18n.t('orders', { ns: 'menu' }),
    url: generatePath(navbarPathsMap.get('clientOrders')!),
    value: navbarPathsMap.get('clientOrders')!,
    icon: 'icon-external-link',
  },
  jobs: {
    name: 'main-menu-jobs-tab',
    title: i18n.t('jobs', { ns: 'menu' }),
    url: generatePath(navbarPathsMap.get('jobs')!),
    value: navbarPathsMap.get('jobs')!,
    icon: 'icons-briefcase',
  },
  invoices: {
    name: 'main-menu-invoices-tab',
    title: i18n.t('invoices', { ns: 'menu' }),
    url: generatePath(navbarPathsMap.get('invoices')!),
    value: navbarPathsMap.get('invoices')!,
    icon: 'icon-dollar',
  },
  reports: {
    name: 'main-menu-reports-tab',
    title: i18n.t('reports', { ns: 'menu' }),
    url: generatePath(iframePaths.reportsTab, {
      tab: 'activities',
    }),
    value: navbarPathsMap.get('reports')!,
    icon: 'icons-chart',
  },
  newsletter: {
    name: 'client-admin-menu-newsletter',
    title: i18n.t('newsletter', { ns: 'menu' }),
    url: process.env.REACT_APP_NAUTIQUS_NEWSLETTER_URL || '',
    value: 'newsletter',
    icon: 'icon-calendar',
  },
  account: {
    name: 'client-admin-menu-account',
    title: i18n.t('account', { ns: 'menu' }),
    url: generatePath(navbarPathsMap.get('account')!),
    value: navbarPathsMap.get('account')!,
    icon: 'icon-user',
  },
  alerts: {
    name: 'client-admin-menu-alerts',
    title: i18n.t('inbox', { ns: 'menu' }),
    url: generatePath(navbarPathsMap.get('home')!),
    value: 'alerts',
    icon: 'icon-envelope',
    alertsContextKey: 'alertsCount',
    // 'icon-envelope-alt'
    // alerts: 'alerts',
    // context: context,
  },
  logout: {
    name: 'logout',
    title: i18n.t('logout', { ns: 'menu' }),
    url: '',
    value: 'logout',
    icon: 'icon-off',
  },
});
